import { defineStore } from 'pinia';
import { reactive } from 'vue';
import {
    hipSpineXraySceneState,
    type HipSpineXraySceneState,
} from '@/views/3d-planner/hipSpine/scene/hipSpineSceneState';

/**
 * Shared scene ViewModel for the hip-spine X-ray review modal.
 *
 * Owns the single `sceneState` instance — the `RenderingState` (camera, viewport
 * size), the image-plane/annotation/angle nodes, and the global brightness/
 * contrast `imageAdjustments` intent. Scene state is presentation state and must
 * not live on the `useHipSpineStore` Model store (docs/threejs-practices.md →
 * "Where scene state lives"; docs/architecture.md → "MVVM for the 3D planner").
 * This mirrors `usePlannerSceneViewModel`, which owns the planner's `sceneState`
 * while `usePlannerStore` holds only domain data.
 *
 * The scene module reads this state through the `SceneContext` (`context.state`);
 * the image-controls VM and the annotations sidebar read it directly through this
 * store. A single shared instance is load-bearing: the image-controls VM both
 * reads and writes `rendering.camera.zoom`, and the scene-update writers read
 * `imageAdjustments` — duplicating the instance would sever the reactive link.
 *
 * Unlike the planner (which rebuilds `sceneState` per case via `resetScene`), the
 * hip-spine scene is reused for the store's lifetime: annotation/angle groups
 * mount and dispose per posture inside `updateHipSpineXrayScene`
 * (docs/threejs-practices.md → "When to break the layer rule"), so the instance
 * itself is stable.
 */
export const useHipSpineXRaySceneViewModel = defineStore('hip-spine-xray-scene', () => {
    const sceneState: HipSpineXraySceneState = reactive(hipSpineXraySceneState());
    return { sceneState };
});
